import { useCallback, useEffect, useMemo, useRef } from "react"
import { debounce } from "lodash"
import { useAppStore } from "hooks/redux"
import {
  useUpdateInstanceWorkspace,
  useUpdateInstanceWorkspaceKeepalive,
} from "resources/dashboard-instances"
import { warn } from "helpers/dev-logger"
import { useIsRealInstance } from "./use-is-real-instance"
import { serializeWorkspace, WORKSPACE_AUTOSAVE_DEBOUNCE_MS } from "../layouts/serialize-workspace"
import type { WorkspaceSerializableState } from "../layouts/serialize-workspace"

// Immer keeps untouched slice refs identical across dispatches, so comparing these cheaply rejects
// unrelated actions (e.g. intraday ticks) before paying for a full serialize + JSON.stringify.
function readWorkspaceSliceRefs(state: WorkspaceSerializableState, dashboardId: string): unknown[] {
  const d = state.dashboard
  return [
    d.panels[dashboardId], d.assetView[dashboardId], d.factorsView[dashboardId],
    d.graph[dashboardId], d.kpi[dashboardId], d.core[dashboardId],
    d.model[dashboardId], d.trade[dashboardId], d.optimization[dashboardId],
  ]
}

function sliceRefsEqual(a: unknown[] | null, b: unknown[]): boolean {
  return a != null && a.length === b.length && a.every((ref, index) => ref === b[index])
}

// Returns the current Dockview arrangement blob (toJSON) for the instance, or null when
// no layout engine is mounted yet.
export type GetArrangement = () => Record<string, unknown> | null

const NO_ARRANGEMENT: GetArrangement = () => null

/**
 * The single workspace writer for an instance (APP-2705). In instance mode (multi-dashboards
 * flag on) it replaces every per-subtype preference PUT: it serializes the whole instance to a
 * WorkspaceStateV1 and PUTs it to `/dashboard/instances/:id/workspace`, debounced trailing-only.
 *
 * - Trailing-only debounce: N rapid edits coalesce into ONE PUT after the quiet window; there is
 *   no leading-edge write.
 * - Hide-flush: on `visibilitychange → hidden` the pending debounce is cancelled and one final
 *   PUT is sent via the keepalive transport so it survives the document being torn down.
 * - Live reads: every save serializes `store.getState()` + the current arrangement at FIRE time,
 *   never a snapshot captured when the save was scheduled (avoids the stale-closure race and a
 *   stale arrangement when the grid changes between scheduling and firing).
 *
 * No-op when the flag is off — unflagged users keep today's per-subtype preference path.
 *
 * @param dashboardId - Key for this instance's slice state; in the routed model it is also the
 *   instance id (a UUID once real), so it addresses the server workspace resource.
 * @param getArrangement - Reads the current Dockview arrangement blob (toJSON) at flush time.
 * @param canPersist - Gate: false suppresses ALL writes (e.g. the instance GET errored, so the
 *   slices hold client defaults — persisting would clobber the server workspace we couldn't load).
 * @returns `requestSave` — schedules a (debounced) save from a NON-Redux source. Dockview
 *   arrangement changes (resize/reorder/split) don't dispatch to the store, so the store
 *   subscription never sees them; the layout-engine persistence calls this on arrangement change.
 */
export function useWorkspaceSync(
  dashboardId: string,
  getArrangement: GetArrangement = NO_ARRANGEMENT,
  canPersist = true,
): () => void {
  // Only a real instance (id always a UUID) has a server-side workspace to autosave; sentinel/slug
  // dashboards render client-side until APP-3155 migrates them. Gate on canPersist too so a failed
  // load (client defaults) stays read-only and never clobbers the server blob.
  const isRealInstance = useIsRealInstance(dashboardId) && canPersist
  const store = useAppStore()
  const putWorkspace = useUpdateInstanceWorkspace(dashboardId)
  const putWorkspaceKeepalive = useUpdateInstanceWorkspaceKeepalive(dashboardId)

  // Keep the arrangement reader fresh without re-creating the debounce/flush callbacks: those
  // capture this ref, not the prop, so a new getArrangement identity never churns the debounce.
  const getArrangementRef = useRef(getArrangement)
  getArrangementRef.current = getArrangement

  // The last serialized JSON we observed — drives change detection and skips no-op saves.
  const lastSerializedRef = useRef<string | null>(null)
  // Last-seen dashboard slice references — the cheap first-stage filter before serializing.
  const lastSliceRefsRef = useRef<unknown[] | null>(null)

  // Serialize the live store + current arrangement into a workspace blob.
  const buildWorkspace = useCallback(() => {
    const state = store.getState() as unknown as WorkspaceSerializableState
    return serializeWorkspace(state, dashboardId, getArrangementRef.current())
  }, [store, dashboardId])

  const debouncedSave = useMemo(
    // Trailing-only (lodash defaults to leading:false): only the settled state writes. Builds FRESH
    // at fire time so a grid change between scheduling and firing is captured; skips a no-op PUT.
    () => debounce(() => {
      const workspace = buildWorkspace()
      const serialized = JSON.stringify(workspace)
      if (serialized === lastSerializedRef.current) return
      lastSerializedRef.current = serialized
      putWorkspace(workspace).catch((error: unknown) => warn("Workspace autosave failed", error))
    }, WORKSPACE_AUTOSAVE_DEBOUNCE_MS),
    [putWorkspace, buildWorkspace],
  )

  // Trigger a save from a non-Redux source (Dockview arrangement changes don't dispatch to the
  // store). No-op when the flag is off / not a real instance.
  const requestSave = useCallback(() => {
    if (!isRealInstance) return
    // Read the live arrangement NOW, while the engine is still mounted on this drag/resize. This
    // warms the caller's last-live cache so an unmount flush BEFORE the debounce fires captures
    // this change rather than the stale seeded arrangement (the engine clears its getter on unmount).
    getArrangementRef.current()
    debouncedSave()
  }, [isRealInstance, debouncedSave])

  // Subscribe to the store; on each change that touches this dashboard's slices, schedule the
  // trailing-edge save. Reading state in the subscriber (not during render) is concurrent-safe.
  useEffect(() => {
    if (!isRealInstance) return undefined
    const onStoreChange = () => {
      // Cheap reject: dispatches that didn't touch this dashboard's slices (Immer keeps unchanged
      // slice refs identical) never schedule — the fire-time serialize + compare skips no-op PUTs.
      const state = store.getState() as unknown as WorkspaceSerializableState
      const sliceRefs = readWorkspaceSliceRefs(state, dashboardId)
      if (sliceRefsEqual(lastSliceRefsRef.current, sliceRefs)) return
      lastSliceRefsRef.current = sliceRefs
      debouncedSave()
    }
    // Capture the baseline so the first store notification after mount isn't treated as an edit.
    const baseState = store.getState() as unknown as WorkspaceSerializableState
    lastSliceRefsRef.current = readWorkspaceSliceRefs(baseState, dashboardId)
    lastSerializedRef.current = JSON.stringify(buildWorkspace())
    const unsubscribe = store.subscribe(onStoreChange)
    return unsubscribe
  }, [isRealInstance, dashboardId, store, buildWorkspace, debouncedSave])

  // Hide-flush: cancel the pending debounce and send the latest state once via keepalive so the
  // final edit isn't lost when the tab is backgrounded/closed.
  useEffect(() => {
    if (!isRealInstance) return undefined
    const onVisibilityChange = () => {
      if (document.visibilityState !== "hidden") return
      debouncedSave.cancel()
      const workspace = buildWorkspace()
      lastSerializedRef.current = JSON.stringify(workspace)
      putWorkspaceKeepalive(workspace).catch((error: unknown) => warn("Workspace hide-flush failed", error))
    }
    document.addEventListener("visibilitychange", onVisibilityChange)
    return () => document.removeEventListener("visibilitychange", onVisibilityChange)
  }, [isRealInstance, buildWorkspace, debouncedSave, putWorkspaceKeepalive])

  // On unmount, FLUSH the pending save rather than cancel it: in-app instance switching remounts
  // (key={dashboardId}) without firing visibilitychange, so an edit/drag still inside the debounce
  // window would otherwise be dropped. flush() no-ops when nothing is pending.
  useEffect(() => () => debouncedSave.flush(), [debouncedSave])

  return requestSave
}
